import {useState, useCallback} from 'react';
import {commitTicketingMutation} from '../../../graphql/forecastRelay';
import UpdateTicketMutation from '../../../../../mutations/ticketing/tickets/updateTicketMutation';
import type {updateTicketMutation} from '../../../../../mutations/ticketing/tickets/__generated__/updateTicketMutation.graphql';
import type {Ticket} from '../types/ticket';

export interface UpdateFieldOptions {
	handoff_note?: string;
}

export interface UseTicketUpdateResult {
	ticket: Ticket;
	saving: boolean;
	error: string | null;
	updateField: (field: keyof Ticket, value: unknown, options?: UpdateFieldOptions) => void;
}

function toInput(field: string, value: unknown, opts: UpdateFieldOptions | undefined): Record<string, unknown> {
	if (field === 'customFields') {
		return {customFields: value};
	}
	const camelField = field === 'assignee_id' ? 'assigneeId' : field;
	const input: Record<string, unknown> = {[camelField]: value};
	if (opts?.handoff_note && opts.handoff_note.trim().length > 0) {
		input['handoffNote'] = opts.handoff_note;
	}
	return input;
}

const OPTIMISTIC_FIELDS = new Set<string>(['status', 'priority', 'channel', 'assignee_id']);

type UpdateTicketResponseTicket = NonNullable<updateTicketMutation['response']['updateTicket']>['ticket'];

function responseToTicket(base: Ticket, gql: NonNullable<UpdateTicketResponseTicket>): Ticket {
	return {
		...base,
		status: gql.status as Ticket['status'],
		priority: gql.priority as Ticket['priority'],
		channel: gql.channel as Ticket['channel'],
		assignee_id: gql.assigneeId ?? null,
		assignee_name: gql.assignee?.fullName ?? null,
	};
}

export function useTicketUpdate(initialTicket: Ticket, onTimelineRefresh?: () => void): UseTicketUpdateResult {
	const [ticket, setTicket] = useState<Ticket>(initialTicket);
	const [error, setError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);

	const updateField = useCallback(
		(field: keyof Ticket, value: unknown, options?: UpdateFieldOptions) => {
			const previous = ticket;
			setError(null);
			setSaving(true);

			const input = {id: previous.id, ...toInput(field, value, options)};

			const isOptimistic = OPTIMISTIC_FIELDS.has(field);
			const optimisticTicket = isOptimistic ? {...previous, [field]: value} : null;

			if (optimisticTicket) {
				setTicket(optimisticTicket);
			}

			const optimisticResponse: Record<string, unknown> | undefined = optimisticTicket
				? {
						updateTicket: {
							clientMutationId: null,
							ticket: {
								id: previous.id,
								status: (optimisticTicket.status as string) ?? previous.status,
								priority: (optimisticTicket.priority as string) ?? previous.priority,
								channel: (optimisticTicket.channel as string) ?? previous.channel,
								assigneeId: (optimisticTicket.assignee_id as string | null) ?? null,
								assignee: null,
							},
							customFields: null,
						},
				  }
				: undefined;

			commitTicketingMutation(
				UpdateTicketMutation,
				{input, optimisticResponse},
				response => {
					setSaving(false);
					const updated = response.updateTicket?.ticket;
					if (updated) {
						setTicket(responseToTicket(previous, updated));
					}
					onTimelineRefresh?.();
				},
				err => {
					setSaving(false);
					setTicket(previous);
					setError(err instanceof Error ? err.message : String(err));
				}
			);
		},
		[ticket, onTimelineRefresh]
	);

	return {ticket, saving, error, updateField};
}
